import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { Check, Copy } from 'lucide-react';
import { api, hlaskaZChyby } from '../api/client.js';
import { formatDatum } from '../lib/format.js';
import { Btn, Chyba, Dialog, Nacitani } from './ui.js';

/**
 * Podpis protistrany přes secure link — dialog vytvoří (nebo zrecykluje)
 * odkaz a nabídne ho dvěma cestami: zkopírovat (poslat mailem/WhatsAppem)
 * nebo QR kód (protistrana naskenuje telefonem a podepíše prstem na místě).
 */
export function PodpisProtistranyDialog({
  entita,
  entitaId,
  nazevDokumentu,
  jmeno,
  onZavrit,
}: {
  entita: 'ZmenovyList' | 'Dodatek' | 'ZjistovaciProtokol';
  entitaId: string;
  nazevDokumentu: string;
  /** Očekávané jméno podepisujícího (např. název subky) — předvyplní se. */
  jmeno?: string;
  onZavrit: () => void;
}) {
  const [odkaz, setOdkaz] = useState<{ url: string; expiresAt: string } | null>(null);
  const [zkopirovano, setZkopirovano] = useState(false);
  const qrRef = useRef<HTMLCanvasElement>(null);

  const vytvor = useMutation({
    mutationFn: () =>
      api.post<{ token: string; expiresAt: string }>('/api/podpisove-odkazy', {
        entita,
        entitaId,
        ...(jmeno ? { jmeno } : {}),
      }),
    onSuccess: (r) =>
      setOdkaz({ url: `${window.location.origin}/podpis/${r.token}`, expiresAt: r.expiresAt }),
  });
  // Jednorázové vytvoření při otevření dialogu (ref brání dvojímu mutate
  // ve StrictMode; vytvor je stabilní z useMutation).
  const spusteno = useRef(false);
  const { mutate: vytvorOdkaz } = vytvor;
  useEffect(() => {
    if (spusteno.current) return;
    spusteno.current = true;
    vytvorOdkaz();
  }, [vytvorOdkaz]);

  useEffect(() => {
    if (odkaz && qrRef.current) {
      void QRCode.toCanvas(qrRef.current, odkaz.url, { width: 220, margin: 1 });
    }
  }, [odkaz]);

  const zkopiruj = async () => {
    if (!odkaz) return;
    await navigator.clipboard.writeText(odkaz.url);
    setZkopirovano(true);
    setTimeout(() => setZkopirovano(false), 3000);
  };

  return (
    <Dialog nadpis="Podpis protistrany" onZavrit={onZavrit}>
      <div className="flex flex-col gap-4">
        <p className="text-base">
          {nazevDokumentu} — protistrana podepíše bez registrace: pošlete jí odkaz, nebo ať
          naskenuje QR kód telefonem a podepíše prstem rovnou tady.
        </p>
        {vytvor.error ? <Chyba>{hlaskaZChyby(vytvor.error)}</Chyba> : null}
        {!odkaz && !vytvor.error && <Nacitani text="Připravuji podpisový odkaz…" />}
        {odkaz && (
          <>
            <div className="flex items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded-md border border-border bg-bg px-2 py-2 text-sm">
                {odkaz.url}
              </code>
              <Btn size="desktop" varianta="secondary" onClick={() => void zkopiruj()}>
                {zkopirovano ? (
                  <>
                    <Check size={16} strokeWidth={2} aria-hidden="true" /> Zkopírováno
                  </>
                ) : (
                  <>
                    <Copy size={16} strokeWidth={2} aria-hidden="true" /> Kopírovat
                  </>
                )}
              </Btn>
            </div>
            <div className="flex flex-col items-center gap-1 rounded-xl border border-border bg-white p-4">
              <canvas ref={qrRef} aria-label="QR kód podpisového odkazu" role="img" />
              <p className="text-sm text-text-secondary">Naskenujte telefonem a podepište</p>
            </div>
            <p className="text-sm text-text-secondary">
              Odkaz platí do {formatDatum(odkaz.expiresAt)}, je jen pro tento dokument a po podpisu
              se zneplatní. Podpis se objeví u dokumentu jako razítko protistrany.
            </p>
          </>
        )}
      </div>
    </Dialog>
  );
}
